import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Home from "@mui/icons-material/Home";
import ResultItem from "../components/ResultItem";

const METRO_CITIES = "Delhi, Mumbai, Kolkata and Chennai";

function HRACalculator() {
  const [basicSalary, setBasicSalary] = useState("");
  const [hraReceived, setHraReceived] = useState("");
  const [rentPaid, setRentPaid] = useState("");
  const [cityType, setCityType] = useState("metro");
  const [result, setResult] = useState(null);

  const amount = (value) => Number(value.replace(/,/g, "")) || 0;

  const formatNumber = (value) => {
    if (!value) return "";
    return Number(value.replace(/,/g, "")).toLocaleString("en-IN");
  };

  const handleAmountChange = (setter) => (event) => {
    setter(event.target.value.replace(/\D/g, ""));
  };

  const calculateHRA = (event) => {
    event.preventDefault();

    const basic = amount(basicSalary);
    const hra = amount(hraReceived);
    const rent = amount(rentPaid);
    const rentLessTenPercent = Math.max(0, rent - basic * 0.1);
    const cityLimit = basic * (cityType === "metro" ? 0.5 : 0.4);
    const exemption = Math.min(hra, rentLessTenPercent, cityLimit);

    setResult({
      hra,
      rent,
      rentLessTenPercent,
      cityLimit,
      exemption,
      taxableHra: Math.max(0, hra - exemption),
    });
  };

  return (
    <Container component="main" sx={{ flex: 1, py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mt: 3,
          pb: 2,
          color: "primary.main",
          fontWeight: 700,
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: 1.2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Home sx={{ fontSize: "2.5rem", color: "primary.dark" }} />
        HRA Calculator
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
        Calculate your annual House Rent Allowance exemption under the old tax regime.
      </Typography>

      <form onSubmit={calculateHRA}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth required label="Annual Basic Salary" value={formatNumber(basicSalary)} onChange={handleAmountChange(setBasicSalary)} helperText="Annual basic salary is the fixed basic component for the year, not total gross salary. It is commonly around 40% to 50% of gross salary, but your employer's salary structure determines the actual amount." />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Annual HRA Received" value={formatNumber(hraReceived)} onChange={handleAmountChange(setHraReceived)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Annual Rent Paid" value={formatNumber(rentPaid)} onChange={handleAmountChange(setRentPaid)} />
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Typography variant="subtitle1" gutterBottom>City of Residence</Typography>
              <RadioGroup row value={cityType} onChange={(event) => setCityType(event.target.value)}>
                <FormControlLabel value="metro" control={<Radio />} label="Metro city (50% limit)" />
                <FormControlLabel value="non-metro" control={<Radio />} label="Non-metro city (40% limit)" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                Metro cities: {METRO_CITIES}.
              </Typography>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit" disabled={!basicSalary || !hraReceived || !rentPaid}>
              Calculate HRA Exemption
            </Button>
          </Grid>
        </Grid>
      </form>

      {result && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>HRA Exemption Details</Typography>
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <ResultItem label="Actual HRA Received" value={result.hra} title="First exemption limit" sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Rent Paid Less 10% of Salary" value={result.rentLessTenPercent} title="Second exemption limit" sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label={`${cityType === "metro" ? "50" : "40"}% of Salary`} value={result.cityLimit} title="Third exemption limit based on your city" sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="HRA Exempt Under Old Regime" value={result.exemption} boldLabel title="The lowest of the three statutory limits" sx={{ bgcolor: "success.light", borderRadius: 1, p: 1 }} />
              </Grid>
            </CardContent>
          </Card>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Under section 10(13A), the exemption is the lowest of actual HRA received, rent paid minus 10% of salary, and 50% of salary for metro cities or 40% for other cities. HRA exemption is available only under the old tax regime.
          </Typography>
        </Box>
      )}

    </Container>
  );
}

export default HRACalculator;
